import { supabase } from '@/lib/supabase/client';

/**
 * Update stock levels and check threshold for re-order alerts
 * Returns SMS link if low stock alert needed
 */
export const updateStockAndCheckThreshold = async (
  itemId: string,
  quantityUsed: number,
  bizPhone: string,
  referenceType?: 'quote' | 'invoice' | 'manual',
  referenceId?: string
) => {
  // 1. Fetch current levels
  const { data: item } = await supabase
    .from('inventory')
    .select('*')
    .eq('id', itemId)
    .single();

  if (!item) return null;

  const newStock = item.current_stock - quantityUsed;

  // 2. Update Database
  const { error: updateError } = await supabase
    .from('inventory')
    .update({ 
      current_stock: newStock,
      last_restocked: newStock > item.current_stock ? new Date().toISOString() : item.last_restocked
    })
    .eq('id', itemId);

  if (updateError) {
    console.error('Failed to update inventory:', updateError);
    return null;
  }

  // 3. Log transaction for audit trail
  await supabase
    .from('inventory_transactions')
    .insert([{
      item_id: itemId,
      transaction_type: 'deduct',
      quantity: quantityUsed,
      reference_type: referenceType || 'manual',
      reference_id: referenceId,
      notes: `Stock deducted from ${referenceType || 'manual'} transaction`,
    }]);

  // 4. Re-order Trigger (The "Ping")
  if (newStock <= item.reorder_point && !item.manager_notified) {
    // Flag that we've notified so we don't spam the manager
    await supabase.from('inventory').update({ manager_notified: true }).eq('id', itemId);

    // Hyperlink out for the "Ping"
    const message = `LOW STOCK ALERT: ${item.item_name} is down to ${newStock} units. Re-order required.`;
    return `sms:${bizPhone}?body=${encodeURIComponent(message)}`;
  }

  return null;
};

/**
 * Add stock to inventory (for restocking)
 */
export const addStock = async (
  itemId: string,
  quantityAdded: number,
  userId?: string
) => {
  const { data: item } = await supabase
    .from('inventory')
    .select('*')
    .eq('id', itemId)
    .single();

  if (!item) return false;

  const newStock = item.current_stock + quantityAdded;

  const { error } = await supabase
    .from('inventory')
    .update({ 
      current_stock: newStock,
      last_restocked: new Date().toISOString(),
      manager_notified: false // Reset notification flag
    })
    .eq('id', itemId);

  if (error) {
    console.error('Failed to add stock:', error);
    return false;
  }

  // Log transaction
  await supabase
    .from('inventory_transactions')
    .insert([{
      item_id: itemId,
      transaction_type: 'add',
      quantity: quantityAdded,
      reference_type: 'manual',
      user_id: userId,
      notes: 'Manual restock',
    }]);

  return true;
};

/**
 * Auto-deduct inventory from quote items
 * Call this when a quote is approved/converted to invoice
 */
export const deductInventoryFromQuote = async (
  quoteId: string,
  bizPhone: string
) => {
  // Fetch quote items
  const { data: items } = await supabase
    .from('quote_items')
    .select('*')
    .eq('quote_id', quoteId);

  if (!items || items.length === 0) return [];

  const alerts: string[] = [];

  // Try to match items to inventory and deduct
  for (const item of items) {
    // Look for inventory items that match the service name
    const { data: inventoryItem } = await supabase
      .from('inventory')
      .select('*')
      .ilike('item_name', `%${item.service_name}%`)
      .single();

    if (inventoryItem) {
      const alert = await updateStockAndCheckThreshold(
        inventoryItem.id,
        item.quantity,
        bizPhone,
        'quote',
        quoteId
      );
      if (alert) alerts.push(alert);
    }
  }

  return alerts;
};

/**
 * Auto-deduct inventory from invoice items
 * Call this when an invoice is marked as paid
 * Now only finalizes previously reserved stock
 */
export const deductInventoryFromInvoice = async (
  invoiceId: string,
  bizPhone: string
) => {
  // Fetch invoice items with inventory_item_id linkage
  const { data: items } = await supabase
    .from('invoice_items')
    .select('*, inventory_item_id')
    .eq('invoice_id', invoiceId);

  if (!items || items.length === 0) return [];

  const alerts: string[] = [];

  // Process items with FK linkage first (priority path)
  for (const item of items) {
    if (item.inventory_item_id) {
      const alert = await updateStockAndCheckThreshold(
        item.inventory_item_id,
        item.quantity,
        bizPhone,
        'invoice',
        invoiceId
      );
      if (alert) alerts.push(alert);
    }
  }

  // Fallback: Try to match items without FK by service name
  for (const item of items.filter(i => !i.inventory_item_id)) {
    const { data: inventoryItem } = await supabase
      .from('inventory')
      .select('*')
      .ilike('item_name', `%${item.service_name}%`)
      .single();

    if (inventoryItem) {
      const alert = await updateStockAndCheckThreshold(
        inventoryItem.id,
        item.quantity,
        bizPhone,
        'invoice',
        invoiceId
      );
      if (alert) alerts.push(alert);
    }
  }

  return alerts;
};

/**
 * Reserve stock for an invoice at creation time
 * Returns warnings for any items with insufficient stock
 */
export const reserveStockForInvoice = async (
  invoiceId: string,
  userId: string
): Promise<{ warnings: string[] }> => {
  const warnings: string[] = [];

  // Fetch invoice items that have inventory_item_id set
  const { data: items } = await supabase
    .from('invoice_items')
    .select('*, inventory_item_id, quantity')
    .eq('invoice_id', invoiceId)
    .not('inventory_item_id', 'is', null);

  if (!items || items.length === 0) {
    return { warnings };
  }

  for (const item of items) {
    if (!item.inventory_item_id) continue;

    // Fetch current inventory item
    const { data: inventoryItem } = await supabase
      .from('inventory')
      .select('*, id, item_name, current_stock, reorder_point, unit')
      .eq('id', item.inventory_item_id)
      .single();

    if (!inventoryItem) continue;

    // Calculate already-reserved quantity for this item
    const { data: reservedTransactions } = await supabase
      .from('inventory_transactions')
      .select('quantity')
      .eq('item_id', item.inventory_item_id)
      .eq('transaction_type', 'reserved')
      .eq('reference_type', 'invoice');

    const alreadyReserved = reservedTransactions?.reduce((sum, t) => sum + t.quantity, 0) || 0;

    // Calculate available stock (current_stock - reserved)
    const available = inventoryItem.current_stock - alreadyReserved;
    const required = item.quantity;

    // Check for shortfall
    if (required > available) {
      const deficit = required - available;
      warnings.push(
        `⚠️ ${inventoryItem.item_name}: Need ${required} ${inventoryItem.unit || 'units'}, have ${available} available — restock ${deficit} ${inventoryItem.unit || 'units'} before job`
      );
    }

    // Insert reservation transaction
    await supabase.from('inventory_transactions').insert([{
      item_id: item.inventory_item_id,
      transaction_type: 'reserved',
      quantity: required,
      reference_type: 'invoice',
      reference_id: invoiceId,
      user_id: userId,
      notes: `Stock reserved for invoice ${invoiceId}`,
    }]);
  }

  // Update invoice to mark stock as reserved
  await supabase
    .from('invoices')
    .update({ stock_reserved: true })
    .eq('id', invoiceId);

  return { warnings };
};

/**
 * Calculate stock shortfalls for quote items
 * Returns array of shortfall warnings
 */
export const calculateQuoteShortfalls = async (
  lineItems: Array<{
    inventory_item_id?: string;
    quantity_required?: number;
    quantity?: number;
  }>
): Promise<Array<{
  item_name: string;
  required: number;
  available: number;
  deficit: number;
  unit: string;
}>> => {
  const shortfalls: Array<{
    item_name: string;
    required: number;
    available: number;
    deficit: number;
    unit: string;
  }> = [];

  for (const item of lineItems) {
    if (!item.inventory_item_id || !item.quantity_required) continue;

    // Fetch inventory item
    const { data: inventoryItem } = await supabase
      .from('inventory')
      .select('*, id, item_name, current_stock, unit')
      .eq('id', item.inventory_item_id)
      .single();

    if (!inventoryItem) continue;

    // Calculate already-reserved quantity
    const { data: reservedTransactions } = await supabase
      .from('inventory_transactions')
      .select('quantity')
      .eq('item_id', item.inventory_item_id)
      .eq('transaction_type', 'reserved');

    const committed = reservedTransactions?.reduce((sum, t) => sum + t.quantity, 0) || 0;
    const available = inventoryItem.current_stock - committed;

    // Calculate required quantity (quantity_required * line item quantity multiplier)
    const required = item.quantity_required * (item.quantity || 1);

    if (required > available) {
      shortfalls.push({
        item_name: inventoryItem.item_name,
        required,
        available,
        deficit: required - available,
        unit: inventoryItem.unit || 'units',
      });
    }
  }

  return shortfalls;
};